import { FC, useMemo, useState, type MouseEvent } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import type { ShopifyRegion } from "../ShopifyGeoSpatialDashboard.data";
import { useLanguage } from "src/hooks/UseLanguage";
import { calculateBarWidthPct } from "src/utils/chartBarScale";
import {
  type DonutHover,
  formatProductBarValue,
  geoNumberText,
  getFixedTooltipPosition,
  PRODUCT_BAR_RANK_COLORS,
} from "./ShopifyGeoSpatialDashboard.utils";
import "../../../styles/ShopifyGeoProductSalesBarChart.scss";

const ShopifyGeoProductSalesBarChart: FC<{
  products: ShopifyRegion["products"];
  onProductClick?: (_productName: string) => void;
}> = ({ products, onProductClick }) => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const [hover, setHover] = useState<DonutHover | null>(null);
  const topProducts = useMemo(
    () => [...products].sort((a, b) => b.value - a.value).slice(0, 5),
    [products]
  );
  const maxValue = useMemo(
    () => Math.max(1, ...topProducts.map(product => product.value)),
    [topProducts],
  );
  const minValue = useMemo(
    () => Math.min(...topProducts.filter(product => product.value > 0).map(product => product.value), 1),
    [topProducts],
  );

  return (
    <div className="shopify-geo__product-bars" aria-label={t("shopifyGeo.donut.aria")}>
      {topProducts.length > 0 ? (
        topProducts.map((product, index) => {
          const width = calculateBarWidthPct(product.value, maxValue, minValue);
          const barColor = PRODUCT_BAR_RANK_COLORS[index % PRODUCT_BAR_RANK_COLORS.length];
          const rowContent = (
            <>
              <span className="shopify-geo__product-bar-meta">
                <span className="shopify-geo__product-bar-name">{product.name}</span>
                <strong>
                  {product.valueFormattedValue ?? formatProductBarValue(product.value, isRTL)}
                </strong>
              </span>
              <span className="shopify-geo__product-bar-track" aria-hidden>
                <span
                  className="shopify-geo__product-bar-fill"
                  style={{ width: `${width}%`, backgroundColor: barColor }}
                />
              </span>
            </>
          );
          const handleMouseMove = (event: MouseEvent<HTMLElement>) => {
            setHover({
              clientX: event.clientX,
              clientY: event.clientY,
              name: product.name,
              value: product.value,
              formattedValue: product.valueFormattedValue,
            });
          };

          if (!onProductClick) {
            return (
              <div
                key={`${product.name}-${product.value}`}
                className="shopify-geo__product-bar-row"
                onMouseMove={handleMouseMove}
                onMouseLeave={() => setHover(null)}
              >
                {rowContent}
              </div>
            );
          }

          return (
            <button
              key={`${product.name}-${product.value}`}
              type="button"
              className="shopify-geo__product-bar-row shopify-geo__product-bar-row--interactive"
              onMouseMove={handleMouseMove}
              onMouseLeave={() => setHover(null)}
              onClick={() => onProductClick(product.name)}
            >
              {rowContent}
            </button>
          );
        })
      ) : (
        <p className="shopify-geo__product-bars-empty">{t("shopifyGeo.productBars.empty")}</p>
      )}
      {hover && createPortal(
        <div
          className="analytics-chart-tooltip analytics-chart-tooltip--shopify-products"
          style={getFixedTooltipPosition(hover.clientX, hover.clientY, 280, 66, 12, 8, isRTL)}
        >
          <div className="analytics-chart-tooltip__bar" />
          <div className="analytics-chart-tooltip__content">
            <span className="analytics-chart-tooltip__meta">
              {t("shopifyGeo.tooltips.productType")} <strong>{hover.name}</strong>
            </span>
            <span className="analytics-chart-tooltip__meta">
              {t("shopifyGeo.tooltips.totalSales")}{" "}
              <strong>
                {hover.formattedValue ?? t("shopifyGeo.tooltips.totalSalesAmount", {
                  amount: geoNumberText(hover.value.toLocaleString(), isRTL),
                })}
              </strong>
            </span>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
};

export default ShopifyGeoProductSalesBarChart;
